
import { useState, useEffect, useCallback } from "react";
import { ContentItem } from "../content-card/types";
import ForceGraph2D from "react-force-graph-2d";
import { useGraphData } from "./network/useGraphData";
import { useGraphBehavior } from "./network/useGraphBehavior";
import { createNodeCanvasObject, createLinkCanvasObject } from "./network/renderFunctions";
import NetworkControls from "./network/NetworkControls";
import EmptyNetworkState from "./network/EmptyNetworkState";
import { useThumbnailPrefetch } from "./network/hooks/useThumbnailPrefetch";
import { useImageCache } from "./network/useImageCache";

interface NetworkViewProps {
  contentItems: ContentItem[];
  collectionColor?: string;
}

const NetworkView = ({ contentItems, collectionColor }: NetworkViewProps) => {
  console.log(`NetworkView received ${contentItems.length} content items`);
  
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [isGraphReady, setIsGraphReady] = useState(false);
  
  const [collectionLinkOpacity, setCollectionLinkOpacity] = useState(() => {
    try {
      const storedValue = localStorage.getItem('network-collection-opacity');
      return storedValue ? parseInt(storedValue, 10) : 100;
    } catch (error) {
      console.error("Error reading collection opacity from localStorage:", error);
      return 100;
    }
  });
  
  const [tagLinkOpacity, setTagLinkOpacity] = useState(() => {
    try {
      const storedValue = localStorage.getItem('network-tag-opacity');
      return storedValue ? parseInt(storedValue, 10) : 100;
    } catch (error) {
      console.error("Error reading tag opacity from localStorage:", error);
      return 100;
    }
  });

  const { imageCache, loadingStates } = useImageCache(contentItems);

  useThumbnailPrefetch(contentItems);

  const { 
    graphData, 
    boundingBox, 
    setBoundingBox,
    collectionLinksCount, 
    tagLinksCount 
  } = useGraphData(contentItems, null, collectionLinkOpacity, tagLinkOpacity);
  
  useEffect(() => {
    if (contentItems.length > 0) {
      const withThumbnails = contentItems.filter(item => item.thumbnail_url && item.thumbnail_url.trim() !== '').length;
      console.log(`Network view thumbnail stats: ${withThumbnails}/${contentItems.length} items have thumbnails (${Math.round(withThumbnails/contentItems.length*100)}%)`);
    }
  }, [contentItems]);
  
  const {
    graphRef,
    pendingDataChange,
    drawBoundingBox,
    centerView,
    applyInitialZoom,
    handleZoomIn,
    handleZoomOut,
    handleCenterView,
    initializeGraphRender,
    forceEngineOptions,
    NODE_BORDER_WIDTH,
    NODE_TEXT_MAX_WIDTH,
    BASE_NODE_WIDTH,
    NODE_ASPECT_RATIO,
    SELECTED_NODE_SCALE,
    HOVERED_NODE_SCALE,
    DEFAULT_NODE_SCALE,
    INITIAL_ZOOM_FACTOR
  } = useGraphBehavior(isGraphReady);

  useEffect(() => {
    try {
      localStorage.setItem('network-collection-opacity', collectionLinkOpacity.toString());
    } catch (error) {
      console.error("Error saving collection opacity to localStorage:", error);
    }
  }, [collectionLinkOpacity]);

  useEffect(() => {
    try {
      localStorage.setItem('network-tag-opacity', tagLinkOpacity.toString());
    } catch (error) {
      console.error("Error saving tag opacity to localStorage:", error);
    }
  }, [tagLinkOpacity]);

  useEffect(() => {
    if (graphData.nodes.length > 0) {
      setIsGraphReady(true);
      console.log(`Graph is ready with ${graphData.nodes.length} nodes and ${graphData.links.length} links`);
      pendingDataChange.current = true;
    }
  }, [graphData.nodes.length, graphData.links.length]);

  useEffect(() => {
    const newBoundingBox = initializeGraphRender(graphData);
    if (newBoundingBox) {
      setBoundingBox(newBoundingBox);
    }
  }, [isGraphReady, graphData, initializeGraphRender, setBoundingBox]);

  const handleNodeHover = useCallback((node: any | null) => {
    setHoveredNode(node ? node.id : null);
  }, []);
  
  const handleNodeClick = useCallback((node: any) => {
    if (node && node.id) {
      console.log(`Node clicked: ${node.id}`);
      
      const contentItem = contentItems.find(item => item.id === node.id);
      if (contentItem && contentItem.url) {
        window.open(contentItem.url, '_blank');
      }
    }
  }, [contentItems]);

  useEffect(() => {
    if (contentItems.length > 0) {
      // Don't clear cache during normal interactions, only when content changes
      // clearImageCache();
    }
  }, [contentItems.length]);

  const nodeCanvasObject = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const renderNode = createNodeCanvasObject(
      hoveredNode,
      null, 
      BASE_NODE_WIDTH,
      NODE_ASPECT_RATIO,
      SELECTED_NODE_SCALE,
      HOVERED_NODE_SCALE,
      DEFAULT_NODE_SCALE,
      NODE_BORDER_WIDTH,
      NODE_TEXT_MAX_WIDTH
    );
    return renderNode(node, ctx, globalScale);
  }, [
    hoveredNode,
    BASE_NODE_WIDTH, 
    NODE_ASPECT_RATIO, 
    SELECTED_NODE_SCALE, 
    HOVERED_NODE_SCALE, 
    DEFAULT_NODE_SCALE,
    NODE_BORDER_WIDTH,
    NODE_TEXT_MAX_WIDTH
  ]);

  const linkCanvasObject = useCallback((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const renderLink = createLinkCanvasObject(collectionLinkOpacity, tagLinkOpacity);
    return renderLink(link, ctx, globalScale);
  }, [collectionLinkOpacity, tagLinkOpacity]);

  const onCenterView = useCallback(() => {
    handleCenterView(graphData);
  }, [handleCenterView, graphData]);

  const handleCollectionOpacityChange = (value: number[]) => {
    const newOpacity = value[0];
    setCollectionLinkOpacity(newOpacity);
  };

  const handleTagOpacityChange = (value: number[]) => {
    const newOpacity = value[0];
    setTagLinkOpacity(newOpacity);
  };

  const onRenderFramePost = useCallback(() => {
    if (boundingBox) {
      drawBoundingBox(boundingBox);
    }
  }, [boundingBox, drawBoundingBox]);

  if (contentItems.length === 0) {
    return <EmptyNetworkState />;
  }

  // Make the container a div with relative positioning (not absolute) to avoid conflicts
  return (
    <div className="relative w-full h-full overflow-hidden">
      <div className="absolute top-2 left-2 z-10 bg-white bg-opacity-75 rounded p-1 text-xs">
        Nodes: {graphData.nodes.length} | Links: {graphData.links.length}
      </div>
      
      <NetworkControls 
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        handleCenterView={onCenterView}
        collectionLinkOpacity={collectionLinkOpacity}
        tagLinkOpacity={tagLinkOpacity}
        handleCollectionOpacityChange={handleCollectionOpacityChange}
        handleTagOpacityChange={handleTagOpacityChange}
        collectionLinksCount={collectionLinksCount}
        tagLinksCount={tagLinksCount}
      />
      
      {/* Change to relative positioning to avoid blocking parent event handlers */}
      <div className="relative w-full h-full">
        <ForceGraph2D
          ref={graphRef}
          graphData={graphData}
          nodeId="id"
          nodeCanvasObject={nodeCanvasObject}
          linkCanvasObject={linkCanvasObject}
          width={window.innerWidth}
          height={window.innerHeight}
          linkDirectionalParticles={0}
          nodeRelSize={6}
          onNodeHover={handleNodeHover}
          onNodeClick={handleNodeClick}
          enableNodeDrag={true}
          enablePanInteraction={true}
          enableZoomInteraction={true}
          cooldownTime={3000}
          onRenderFramePost={onRenderFramePost}
          nodeLabel={null}
          d3AlphaDecay={0.015}
          d3VelocityDecay={0.2}
          // Configure the force engine properly without using d3Force
          {...forceEngineOptions}
        />
      </div>
    </div>
  );
};

export default NetworkView;
